import type { Coin } from "@akashnetwork/chain-sdk/private-types/cosmos.v1beta1";
import type { NetworkId } from "@akashnetwork/chain-sdk/web";
import add from "date-fns/add";

import { READABLE_DENOMS, UACT_DENOM, UAKT_DENOM, USDC_IBC_DENOMS } from "@src/config/denom.config";
import networkStore from "@src/store/networkStore";
import { averageDaysInMonth } from "./dateUtils";
import { denomToUdenom, udenomToDenom } from "./mathHelpers";

export const averageBlockTime = 6.098;

/** Converts a per-block price to its hourly rate. Block time is in seconds, so there are `3600 / averageBlockTime` blocks per hour. */
export function perBlockToHourly(perBlockValue: number): number {
  return perBlockValue * (60 / averageBlockTime) * 60;
}

export const getUsdcDenom = (networkId?: NetworkId): string => {
  return USDC_IBC_DENOMS[networkId ?? (networkStore.selectedNetworkId as keyof typeof USDC_IBC_DENOMS)] as string;
};

/** @deprecated use udenomToDenom */
export function uaktToAKT(amount: number, precision = 3) {
  return udenomToDenom(amount, precision);
}

/** @deprecated use denomToUdenom */
export function aktToUakt(amount: number | string) {
  return denomToUdenom(String(amount));
}

export function coinToUDenom(coin: Coin) {
  let value: number | null;
  const usdcDenom = getUsdcDenom();

  if (coin.denom === "akt" || coin.denom === "act") {
    value = denomToUdenom(coin.amount);
  } else if (coin.denom === UAKT_DENOM || coin.denom === usdcDenom || coin.denom === UACT_DENOM) {
    value = parseFloat(coin.amount);
  } else {
    throw Error("Unrecognized denom: " + coin.denom);
  }

  return value;
}

export function coinToDenom(coin: Coin) {
  let value: number | null;
  const usdcDenom = getUsdcDenom();

  if (coin.denom === "akt" || coin.denom === "act") {
    value = parseFloat(coin.amount);
  } else if (coin.denom === UAKT_DENOM || coin.denom === usdcDenom || coin.denom === UACT_DENOM) {
    value = udenomToDenom(coin.amount);
  } else {
    throw Error("Unrecognized denom: " + coin.denom);
  }

  return value;
}

export function getAvgCostPerMonth(pricePerBlock: number) {
  return (pricePerBlock * averageDaysInMonth * 24 * 60 * 60) / averageBlockTime;
}

type PricedLease = { price: { denom: string; amount: string | number } };

/**
 * Sums the per-block price of the given leases in USD. Pass already-live leases. Deployments are only ever
 * funded in ACT (1:1 USD); any other denom is ignored.
 */
export function getLeasesCostPerBlockUsd(leases: PricedLease[]): number {
  return leases.reduce((total, { price }) => {
    if (price.denom !== UACT_DENOM) return total;
    return total + udenomToDenom(price.amount, 10);
  }, 0);
}

/**
 * Groups the per-block USD price of the given leases by the deployment they belong to. Pass already-live leases.
 */
export function getLeaseCostPerBlockUsdByDseq(leases: (PricedLease & { dseq: string })[]): Map<string, number> {
  const perBlockUsdByDseq = new Map<string, number>();

  for (const lease of leases) {
    perBlockUsdByDseq.set(lease.dseq, (perBlockUsdByDseq.get(lease.dseq) ?? 0) + getLeasesCostPerBlockUsd([lease]));
  }

  return perBlockUsdByDseq;
}

/**
 * Nets off what a provider has earned since the escrow last settled. The chain only decrements an escrow's
 * funds on settlement (deposit, withdrawal, lease close), so between settlements the raw figure reads high.
 * Units follow the inputs: pass the balance and the price both in udenom, or both in USD.
 * Falls back to the settled balance when the height is unknown or nothing is being spent.
 */
export function getLiveEscrowBalance({
  settledBalance,
  pricePerBlock,
  settledAt,
  latestBlockHeight
}: {
  settledBalance: number;
  pricePerBlock: number;
  settledAt: number;
  latestBlockHeight?: number;
}): number {
  if (latestBlockHeight === undefined || !Number.isFinite(latestBlockHeight) || !Number.isFinite(settledAt) || !pricePerBlock) return settledBalance;

  const blocksSinceSettlement = Math.max(latestBlockHeight - settledAt, 0);

  return Math.max(settledBalance - blocksSinceSettlement * pricePerBlock, 0);
}

export function getTimeLeft(pricePerBlock: number, balance: number) {
  const blocksLeft = balance / pricePerBlock;
  const timestamp = new Date().getTime();
  return add(new Date(timestamp), { seconds: blocksLeft * averageBlockTime });
}

export function toReadableDenom(denom: string) {
  return READABLE_DENOMS[denom];
}
